import i18n from "assets/i18n";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import type { TFunction } from "i18next";

import type { GroupProgress, MilestonePrize, Prize, Trigger } from "../apis/models";
import { RewardStatusType, RewardType, TriggerId } from "../models/constants";

dayjs.extend(duration);
dayjs.extend(utc);

export const ellipsis = (text: string, size: number) => {
  return text.length > size ? `${text.slice(0, size)}...` : text;
};

export const returnTriggerType = (trigger: Trigger | undefined, translate: TFunction<"translation">) => {
  switch (trigger?.triggerType) {
  case TriggerId.TIER:
    return translate("upgrade_your_tier");
  default:
    return "";
  }
};

export const keyValArrayToObject = (array: { key: string; value: any }[]) => {
  return array.reduce(
    (obj, item) => {
      obj[item.key] = item.value;
      return obj;
    },
    {} as { [key: string]: any },
  );
};

export const formatNumber = (number: number) => {
  return number.toLocaleString("en-US");
};

export const compactNumber = (value: number) => {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
  }).format(value);
};

export const normalizeBy = <T extends Record<string, any>>(
  array: T[],
  key: string | number,
) => {
  return array.reduce(
    (obj, item) => {
      obj[item[key]] = item;
      return obj;
    },
    {} as { [key: string | number]: T },
  );
};

export const getLargestUncompleted = (
  groupProgress: GroupProgress[],
): Trigger => {
  let triggers = groupProgress
    ?.map((group) => group?.triggersProgress?.map((trigger) => trigger))
    .flat();

  if (!triggers.length) return {} as Trigger;

  const res = triggers.reduce((prev, cur) => {
    if (triggers.length > 1) {
      if (prev.progress === 100) return cur;
    }
    return cur.progress < 100 && cur.progress > prev.progress ? cur : prev;
  });

  return res;
};

const getMilestoneEntryStatus = (entry?: MilestonePrize) => entry?.prizes?.at(0)?.status;

// Ties among entries sharing the same status are resolved by API response
// order (the backend returns milestone entries in reached order), not by
// milestoneId — MilestonePrize carries no other reliable ordering signal.
export const getActiveMilestonePrize = (
  milestonePrizes: MilestonePrize[] | undefined,
): MilestonePrize | undefined => {
  if (!milestonePrizes?.length) return undefined;

  const validEntry = milestonePrizes.find(
    (entry) => getMilestoneEntryStatus(entry) === RewardStatusType.VALID,
  );
  if (validEntry) return validEntry;

  const pendingOrProcessingEntry = milestonePrizes.find((entry) => {
    const status = getMilestoneEntryStatus(entry);
    return status === RewardStatusType.PENDING || status === RewardStatusType.PROCESSING;
  });
  if (pendingOrProcessingEntry) return pendingOrProcessingEntry;

  // No entry is Valid or Pending/Processing (e.g. all Expired/Burned) — nothing
  // better to surface, so fall back to the first entry as before this fix.
  return milestonePrizes[0];
};

// A Pending/Processing prize has no value issued yet, so value is what separates a
// reward the user can actually use from one the backend is still generating.
// Flattens because the valid prize is not always the entry's first.
export const getShowablePrize = (
  milestonePrizes: MilestonePrize[] | undefined,
): Prize | undefined =>
  milestonePrizes
    ?.flatMap((entry) => entry?.prizes ?? [])
    .find((prize) => prize?.status === RewardStatusType.VALID && !!prize.value?.trim());

export const isPendingOrProcessingPrize = (milestonePrizes: MilestonePrize[] | undefined) => {
  const status = getActiveMilestonePrize(milestonePrizes)?.prizes?.at(0)?.status;
  return status === RewardStatusType.PENDING || status === RewardStatusType.PROCESSING;
};

export const getRewardType = (
  translate: TFunction<"translation">,
  type: number,
  rewardValue?: string
) => {
  if (type === RewardType.VOUCHER) return translate("vouchers");
  if (type === RewardType.BADGE) return translate("badge");
  if (type === RewardType.POINTS_MULTIPLIER) return `${rewardValue}x ${translate("points_2")}`;
  return toLocalizedNumber(Number(rewardValue || 0)) + " " + translate("points");
};

export const getQuantity = (translate: TFunction<"translation">, quantity: number | undefined = 0) => {
  if (quantity === 1) return translate("day");
  else if (quantity === 2) return translate("two_days");
  else if (quantity <= 10) return translate("days");
  else return translate("day");
};

export const toLocalizedNumber = (num: number): string => {
  if (i18n.language.includes("ar")) {
    return num.toString().replaceAll(/\d/g, (digit) => "٠١٢٣٤٥٦٧٨٩"[Number.parseInt(digit)]);
  }
  return num.toString();
};

export const formatRemainingTime = (endDate: string | undefined | null, translate: TFunction<"translation">) => {
  if (!endDate) return "0" + translate("minute_shortcut");

  // Parse the endDate - treat as UTC if no timezone info present
  const endDateTime = dayjs.utc(endDate);
  const now = dayjs.utc();

  const ms = endDateTime.diff(now);

  if (Number.isNaN(ms) || ms <= 0) return "0" + translate("minute_shortcut");

  const parts: string[] = [];
  const dayLabel = translate("day_shortcut");
  const hourLabel = translate("hour_shortcut");
  const minuteLabel = translate("minute_shortcut");

  // Calculate total days by dividing milliseconds by milliseconds in a day
  const totalDays = Math.floor(ms / (1000 * 60 * 60 * 24));
  // Calculate remaining hours after accounting for days
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  // Calculate remaining minutes after accounting for days and hours
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

  if (totalDays > 0) parts.push(`${toLocalizedNumber(totalDays)}${dayLabel}`);
  if (hours > 0) parts.push(`${toLocalizedNumber(hours)}${hourLabel}`);
  parts.push(`${toLocalizedNumber(minutes)}${minuteLabel}`);

  return parts.join(" ");
};
